import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Modal,
  Radio,
  RadioGroup,
  Typography,
} from "@material-ui/core";
import { ModalStyles } from "../../styles";
import { useFormik } from "formik";
import { Api } from "../../lib/api";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import { useSnackbar } from "notistack";
import { UrlStatus } from "@authory-packages/core";
import { getUrlStatusLabel } from "../../lib/labelNames";

type MoveUrlModalProps = {
  open: boolean;
  onClose: () => void;
  userSlug: string;
  websiteSlug: string;
};

export const MoveUrlModal = ({
  open,
  onClose,
  userSlug,
  websiteSlug,
}: MoveUrlModalProps) => {
  const modalStyles = ModalStyles();

  const { enqueueSnackbar } = useSnackbar();

  const formik = useFormik({
    initialValues: {
      accepted: false,
      rejected: false,
      permanentError: false,
      to: UrlStatus.StaticAnalyze,
    },
    onSubmit: async (data) => {
      if (data.accepted) {
        try {
          const res = await Api.moveURLS(
            userSlug,
            websiteSlug,
            UrlStatus.Accepted,
            data.to
          );
          enqueueSnackbar(`Moved ${res.data.count} urls from accepted`, {
            variant: "success",
          });
        } catch (error: any) {
          enqueueSnackbar(error.response.data.message, { variant: "error" });
        }
      }

      if (data.rejected) {
        try {
          const res = await Api.moveURLS(
            userSlug,
            websiteSlug,
            UrlStatus.Rejected,
            data.to
          );
          enqueueSnackbar(`Moved ${res.data.count} urls from rejected`, {
            variant: "success",
          });
        } catch (error: any) {
          enqueueSnackbar(error.response.data.message, { variant: "error" });
        }
      }

      if (data.permanentError) {
        try {
          const res = await Api.moveURLS(
            userSlug,
            websiteSlug,
            UrlStatus.PermanentError,
            data.to
          );
          enqueueSnackbar(`Moved ${res.data.count} urls from permanent error`, {
            variant: "success",
          });
        } catch (error: any) {
          enqueueSnackbar(error.response.data.message, { variant: "error" });
        }
      }
      onClose();
    },
    enableReinitialize: true,
  });

  return (
    <Modal open={open} onClose={onClose}>
      <div className={modalStyles.paper}>
        <>
          <Box mb={4}>
            <Typography variant="h4" component="h4">
              Move URLs
            </Typography>
          </Box>
          <form noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
            <Grid container>
              <Grid item xs={6}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Typography variant="h5">From</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                          checkedIcon={<CheckBoxIcon fontSize="small" />}
                          name="accepted"
                          checked={formik.values.accepted}
                          onChange={formik.handleChange}
                        />
                      }
                      label="Accepted"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                          checkedIcon={<CheckBoxIcon fontSize="small" />}
                          name="rejected"
                          checked={formik.values.rejected}
                          onChange={formik.handleChange}
                        />
                      }
                      label="Rejected"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                          checkedIcon={<CheckBoxIcon fontSize="small" />}
                          name="permanentError"
                          checked={formik.values.permanentError}
                          onChange={formik.handleChange}
                        />
                      }
                      label="Permanent Error"
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={6}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Typography variant="h5">To</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <RadioGroup
                      name="to"
                      value={formik.values.to}
                      onChange={formik.handleChange}
                    >
                      <FormControlLabel
                        value={UrlStatus.StaticAnalyze}
                        control={<Radio />}
                        label={getUrlStatusLabel(UrlStatus.StaticAnalyze)}
                      />
                      <FormControlLabel
                        value={UrlStatus.EarlyRejectCheck}
                        control={<Radio />}
                        label={getUrlStatusLabel(UrlStatus.EarlyRejectCheck)}
                      />
                      <FormControlLabel
                        value={UrlStatus.DiffbotAnalyze}
                        control={<Radio />}
                        label={getUrlStatusLabel(UrlStatus.DiffbotAnalyze)}
                      />
                      <FormControlLabel
                        value={UrlStatus.DynamicAnalyze}
                        control={<Radio />}
                        label={getUrlStatusLabel(UrlStatus.DynamicAnalyze)}
                      />
                      <FormControlLabel
                        value={UrlStatus.LLMAnalyze}
                        control={<Radio />}
                        label={getUrlStatusLabel(UrlStatus.LLMAnalyze)}
                      />
                      <FormControlLabel
                        value={UrlStatus.ShivaContentExtraction}
                        control={<Radio />}
                        label={getUrlStatusLabel(
                          UrlStatus.ShivaContentExtraction
                        )}
                      />
                      <FormControlLabel
                        value={UrlStatus.DynamicAnalyzeCheck}
                        control={<Radio />}
                        label={getUrlStatusLabel(UrlStatus.DynamicAnalyzeCheck)}
                      />
                    </RadioGroup>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Box mt={2}>
              <Button
                variant="contained"
                onClick={onClose}
                style={{ marginRight: 20 }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => formik.submitForm()}
              >
                Move
              </Button>
            </Box>
          </form>
        </>
      </div>
    </Modal>
  );
};
